import React, { useState } from 'react';
import { List, Checkbox, Button } from 'antd';
import classNames from 'classnames';
import { CheckOutlined, LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { BulkQuickFixItem } from '../../../../types/analyses';

interface ProductListProps {
  items: BulkQuickFixItem[];
  onConfirm: (itemIds: number[]) => void;
  onDecline: (itemIds: number[]) => void;
  onShowProducts: (itemIds: number[]) => void;
  isLoading: boolean;
  isDeclining?: boolean;
}

const ProductList: React.FC<ProductListProps> = ({
  items,
  onConfirm,
  onDecline,
  onShowProducts,
  isLoading,
  isDeclining = false,
}) => {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [acceptedItemPartAttributeIds, setAcceptedItemPartAttributeIds] = useState<number[]>([]);
  const [declinedItemPartAttributeIds, setDeclinedItemPartAttributeIds] = useState<number[]>([]);

  if (!items || items.length === 0) return null;

  const selectRow = (itemPartAttributeId: number) => {
    // Don't allow selection of processed items
    if (
      acceptedItemPartAttributeIds.includes(itemPartAttributeId) ||
      declinedItemPartAttributeIds.includes(itemPartAttributeId)
    ) {
      return;
    }

    if (!selectedIds.includes(itemPartAttributeId)) {
      setSelectedIds([...selectedIds, itemPartAttributeId]);
    } else {
      setSelectedIds(selectedIds.filter(id => id !== itemPartAttributeId));
    }
  };

  const selectAllItems = () => {
    // Only select items that haven't been processed
    const availableItems = items.filter(
      item =>
        !acceptedItemPartAttributeIds.includes(item.item_part_attribute_id) &&
        !declinedItemPartAttributeIds.includes(item.item_part_attribute_id)
    );
    const availableItemPartAttributeIds = availableItems.map(item => item.item_part_attribute_id);

    if (selectedIds.length === availableItemPartAttributeIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(availableItemPartAttributeIds);
    }
  };

  const selectCount = selectedIds.length;
  const availableItemCount =
    items.length - acceptedItemPartAttributeIds.length - declinedItemPartAttributeIds.length;
  const itemCount = items.length;

  const header = (
    <div>
      <div className="analyses__item-list-header-checkbox">
        <Checkbox
          className="analyses__item-list-check"
          indeterminate={selectCount > 0 && selectCount < availableItemCount}
          checked={selectCount > 0 && selectCount === availableItemCount}
          onChange={() => selectAllItems()}
        />
        <span className="text-gray-600 ml-2">
          {`${itemCount} ${t('quickfix:item', { count: itemCount })}`}
        </span>
      </div>
    </div>
  );

  const footer = (
    <div className="analyses__item-button-wrapper">
      <Button
        type="primary"
        ghost
        size="small"
        className="analyses__button-confirm"
        icon={isLoading ? <LoadingOutlined /> : <CheckOutlined />}
        onClick={() => {
          // Pass selected item_part_attribute_ids directly
          const selectedItemPartAttributeIds = selectedIds;
          onConfirm(selectedItemPartAttributeIds);
          setAcceptedItemPartAttributeIds([
            ...acceptedItemPartAttributeIds,
            ...selectedItemPartAttributeIds,
          ]);
          setSelectedIds([]);
        }}
        loading={isLoading}
        disabled={selectedIds.length === 0 || isLoading}
      >
        {isLoading ? t('quickfix:accepting') : t('quickfix:acceptSuggestion')}
      </Button>
      <Button
        danger
        ghost
        size="small"
        className="analyses__button-decline"
        disabled={selectedIds.length === 0 || isDeclining}
        loading={isDeclining}
        onClick={() => {
          // Pass selected item_part_attribute_ids directly
          const selectedItemPartAttributeIds = selectedIds;
          onDecline(selectedItemPartAttributeIds);
          setDeclinedItemPartAttributeIds([
            ...declinedItemPartAttributeIds,
            ...selectedItemPartAttributeIds,
          ]);
          setSelectedIds([]);
        }}
      >
        {isDeclining ? t('quickfix:declining') : t('common:decline')}
      </Button>
      <Button
        size="small"
        className="analyses__button-add-session"
        disabled={selectedIds.length === 0}
        onClick={() => {
          // Convert selected item_part_attribute_ids to item_ids for the callback
          const itemIds = items
            .filter(item => selectedIds.includes(item.item_part_attribute_id))
            .map(item => item.item_id);
          onShowProducts(itemIds);
        }}
      >
        {t('quickfix:addToFilter')}
      </Button>
    </div>
  );

  return (
    <List
      className="analyses__item-list"
      bordered
      size="small"
      header={header}
      footer={footer}
      dataSource={items}
      renderItem={item => {
        const isSelected = selectedIds.includes(item.item_part_attribute_id);
        const isAccepted = acceptedItemPartAttributeIds.includes(item.item_part_attribute_id);
        const isDeclined = declinedItemPartAttributeIds.includes(item.item_part_attribute_id);

        const cssClasses = classNames(
          { analyses__accepted: isAccepted },
          { analyses__declined: isDeclined }
        );

        return (
          <List.Item className={cssClasses} onClick={() => selectRow(item.item_part_attribute_id)}>
            <Checkbox
              className="analyses__item-list-check"
              checked={isSelected}
              onChange={() => selectRow(item.item_part_attribute_id)}
              disabled={isAccepted || isDeclined}
            />
            <div className="analyses__item-list-name">{item.part_number}</div>
            <div className="analyses__item-list-name">
              {item.part_attribute_value && (
                <span className="text-gray-500 ml-2">({item.part_attribute_value})</span>
              )}
            </div>
          </List.Item>
        );
      }}
    />
  );
};

export default ProductList;
